import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, dataApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionCard, KpiCard } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LinkedDocsTab from "@/components/LinkedDocsTab";
import ProjectDiscussion from "@/components/chat/ProjectDiscussion";
import { BranchTag, useBranches } from "@/components/BranchBits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, TrendingUp, Wallet, CheckCircle2, Clock, Github, Gitlab, GitFork, RefreshCw, FileText, Download, Trash2, Upload, Users } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, Legend, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

const PLATFORM_ICON = { github: Github, gitlab: Gitlab, bitbucket: GitFork };

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function UploadFileDialog({ open, onOpenChange, projectId, members, clients, onSaved }) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [selected, setSelected] = useState(() => new Set()); // empty = all
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (open) { setTitle(""); setFile(null); setSelected(new Set()); setQ(""); } }, [open]);

  const all = useMemo(() => {
    const seen = new Set();
    const rows = [];
    for (const m of members || []) if (m && m.id && !seen.has(m.id)) { seen.add(m.id); rows.push({ ...m, _kind: "employee" }); }
    for (const c of clients || []) if (c && c.user_id && !seen.has(c.user_id)) { seen.add(c.user_id); rows.push({ id: c.user_id, full_name: `${c.name} (Client)`, _kind: "client" }); }
    return rows;
  }, [members, clients]);
  const filtered = all.filter((r) => !q || r.full_name.toLowerCase().includes(q.toLowerCase()));

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    if (!file) return toast.error("Pick a file to upload");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("title", title.trim());
      form.append("file", file);
      if (selected.size > 0) form.append("allowed_user_ids", JSON.stringify(Array.from(selected)));
      await api.post(`/projects/${projectId}/files`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="upload-file-dialog">
        <DialogHeader>
          <DialogTitle>Upload project file</DialogTitle>
          <DialogDescription>Share a document with the whole project team or restrict access to specific people.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input data-testid="file-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Kick-off deck" /></div>
          <div>
            <Label>File *</Label>
            <Input data-testid="file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {file && <div className="text-xs text-muted-foreground mt-1">{file.name} · {fmtBytes(file.size)}</div>}
          </div>
          <div>
            <Label className="flex items-center justify-between">
              <span>Allowed users (optional)</span>
              <span className="text-[10px] text-muted-foreground">{selected.size === 0 ? "Everyone with project access" : `${selected.size} selected`}</span>
            </Label>
            <Input placeholder="Search employees or clients…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 mb-2" />
            <div className="max-h-52 overflow-y-auto scroll-thin border rounded-md">
              {filtered.length === 0 && <div className="text-xs text-muted-foreground p-3 text-center">No people to pick — leave blank so everyone with access can download.</div>}
              {filtered.map((r) => (
                <label key={r.id} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent cursor-pointer">
                  <Checkbox data-testid={`file-user-${r.id}`} checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} />
                  <span className="flex-1 truncate">{r.full_name}</span>
                  <span className="text-[10px] uppercase text-muted-foreground">{r._kind}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>Cancel</Button>
          <Button data-testid="file-upload-submit" onClick={submit} disabled={uploading}><Upload className="w-4 h-4 mr-1" />{uploading ? "Uploading…" : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectFilesTab({ projectId, isAdmin, members }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);

  const load = () => {
    setLoading(true);
    api.get(`/projects/${projectId}/files`).then((r) => setFiles(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    if (isAdmin) api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  }, [projectId, isAdmin]);

  const download = async (f) => {
    try {
      // Auth-safe download: fetch as blob using axios interceptor tokens.
      const res = await api.get(`/projects/${projectId}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const remove = async (f) => {
    if (!confirm(`Delete "${f.title}"?`)) return;
    try { await api.delete(`/projects/${projectId}/files/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <SectionCard
      title="Files"
      subtitle={`${files.length} document${files.length === 1 ? "" : "s"}`}
      actions={isAdmin && (
        <Button size="sm" data-testid="proj-add-file" onClick={() => setOpen(true)}>
          <Upload className="w-4 h-4 mr-1" />Add File
        </Button>
      )}
    >
      {loading ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
      ) : files.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">
          {isAdmin ? "No files uploaded yet. Click Add File to upload your first document." : "No files have been shared with you yet."}
        </div>
      ) : (
        <div className="divide-y divide-border/50">
          {files.map((f) => (
            <div key={f.id} data-testid={`proj-file-${f.id}`} className="flex items-center gap-3 py-2.5">
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate" title={f.title}>{f.title}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {f.filename} · {fmtBytes(f.size)} · {fmtDateTime(f.created_at)}
                  {f.uploaded_by_name && <> · uploaded by {f.uploaded_by_name}</>}
                </div>
              </div>
              {f.is_public ? (
                <Badge variant="outline" className="text-[10px]"><Users className="w-3 h-3 mr-1" />Everyone</Badge>
              ) : (
                <Badge className="bg-primary/15 text-primary border-0 rounded-md text-[10px]" title={`${f.allowed_user_ids.length} users`}>
                  <Users className="w-3 h-3 mr-1" />Restricted · {f.allowed_user_ids.length}
                </Badge>
              )}
              <Button data-testid={`file-download-${f.id}`} size="sm" variant="outline" onClick={() => download(f)}>
                <Download className="w-3 h-3 mr-1" />Download
              </Button>
              {isAdmin && (
                <Button data-testid={`file-delete-${f.id}`} size="sm" variant="ghost" className="text-red-500 hover:text-red-500" onClick={() => remove(f)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {isAdmin && (
        <UploadFileDialog
          open={open}
          onOpenChange={setOpen}
          projectId={projectId}
          members={members}
          clients={clients}
          onSaved={load}
        />
      )}
    </SectionCard>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [git, setGit] = useState(null);
  const [params, setParams] = useSearchParams();
  const { byId: branchById } = useBranches();
  const canDiscuss = user?.role !== "client";
  // Discussion is the default landing tab (clients don't get it).
  const tab = params.get("tab") || (canDiscuss ? "discussion" : "overview");

  const loadGit = () => api.get(`/git/projects/${id}?days=90`).then((r) => setGit(r.data)).catch(() => {});

  useEffect(() => {
    dataApi.project(id).then((r) => setData(r.data));
    loadGit();
  }, [id]);

  const syncRepo = async (repoId) => {
    try {
      await api.post(`/git/repositories/${repoId}/sync`);
      toast.info("Sync started");
      setTimeout(loadGit, 2000);
    } catch (e) {
      toast.error("Sync failed");
    }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const p = data.project;
  const m = data.metrics;
  // A user is "privileged" on this project if they are an admin OR the
  // project manager. Everyone else sees a slimmer view: no Budget vs Actual
  // card and only their own tasks in the Tasks tab.
  const isProjectManager = !!user && p.manager_id === user.id;
  const canSeePrivate = isAdmin || isProjectManager || user?.role === "branch_admin"; // branch admins view all branches
  const canManageFiles = isAdmin || (user?.role === "branch_admin" && !!p.can_modify);
  const visibleTasks = canSeePrivate
    ? data.tasks
    : data.tasks.filter((t) => t.assignee_id === user?.id || t.reporter_id === user?.id);

  // Fake burndown from milestones
  const burndown = (p.milestones || []).map((ms, i) => ({
    name: ms.name,
    planned: 100 - (i + 1) * 25,
    actual: Math.max(0, 100 - (ms.progress + i * 15)),
  }));

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => nav("/projects")}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display font-semibold text-3xl tracking-tighter">{p.name}</h1>
              <Badge variant="outline" className="capitalize">{p.status.replace("_", " ")}</Badge>
              <BranchTag id={p.branch_id} byId={branchById} />
            </div>
            <div className="text-muted-foreground mt-1">
              {p.client} · {p.code} · Repository: {p.repository_url}
            </div>
            {data.manager && (
              <div className="text-xs text-muted-foreground mt-1" data-testid="proj-manager">
                Project Manager: <span className="font-medium text-foreground">{data.manager.full_name}</span>
                {data.manager.designation && <span> · {data.manager.designation}</span>}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="label-caps">Health</div>
            <div className="number text-4xl mt-1">{p.health_score}</div>
          </div>
        </div>
        <div className="mt-4">
          <Progress value={p.progress} className="h-2" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>Progress {p.progress}%</span>
            <span>Planned {p.planned_progress}%</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Completion" value={m.task_completion} suffix="%" icon={CheckCircle2} />
        {canSeePrivate && (
          <KpiCard label="Budget Burn" value={m.budget_burn} suffix="%" icon={Wallet} tone={m.budget_burn > 100 ? "danger" : "default"} />
        )}
        <KpiCard label="Sched Variance" value={m.schedule_variance} suffix="%" icon={TrendingUp} tone={m.schedule_variance < 0 ? "danger" : "success"} />
        {canSeePrivate && (
          <KpiCard label="Cost Variance" value={m.cost_variance} icon={Clock} />
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList>
          {canDiscuss && <TabsTrigger value="discussion" data-testid="proj-discussion-tab">Discussion</TabsTrigger>}
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="milestones">Milestones</TabsTrigger>
          <TabsTrigger value="files" data-testid="proj-files-tab">Files</TabsTrigger>
          <TabsTrigger value="tasks">Tasks ({visibleTasks.length})</TabsTrigger>
          <TabsTrigger value="repos" data-testid="proj-repos-tab">Repositories ({git?.repositories?.length || 0})</TabsTrigger>
          <TabsTrigger value="docs" data-testid="proj-docs-tab">Documentation</TabsTrigger>
          <TabsTrigger value="members">Members ({data.members.length})</TabsTrigger>
        </TabsList>

        {canDiscuss && (
          <TabsContent value="discussion" className="pt-6">
            <ProjectDiscussion projectId={id} />
          </TabsContent>
        )}

        <TabsContent value="overview" className="pt-6 grid lg:grid-cols-2 gap-6">
          <SectionCard title="Burndown">
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={burndown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="planned" stroke="hsl(220 8% 55%)" strokeDasharray="5 5" name="Planned" />
                  <Line type="monotone" dataKey="actual" stroke="hsl(211 100% 55%)" strokeWidth={2.5} name="Actual" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>
          {canSeePrivate && (
            <SectionCard title="Budget vs Actual">
              <div className="space-y-4 py-4">
                <div>
                  <div className="flex justify-between text-sm mb-1"><span>Planned</span><span className="number">${p.planned_cost.toLocaleString()}</span></div>
                  <Progress value={100} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1"><span>Actual</span><span className="number">${p.actual_cost.toLocaleString()}</span></div>
                  <Progress value={m.budget_burn} className="h-2" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1"><span>Total Budget</span><span className="number">${p.budget.toLocaleString()}</span></div>
                </div>
              </div>
            </SectionCard>
          )}
        </TabsContent>

        <TabsContent value="milestones" className="pt-6">
          <SectionCard title="Milestones">
            <div className="space-y-3">
              {p.milestones.map((ms) => (
                <div key={ms.id} className="flex items-center gap-4 py-2">
                  <div className={`h-2.5 w-2.5 rounded-full ${ms.completed ? "bg-emerald-500" : "bg-muted-foreground"}`} />
                  <div className="w-40 font-medium text-sm">{ms.name}</div>
                  <Progress value={ms.progress} className="h-2 flex-1" />
                  <div className="w-12 text-right number text-sm">{ms.progress}%</div>
                  <div className="w-24 text-right text-xs text-muted-foreground number">{ms.due_date}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="files" className="pt-6">
          <ProjectFilesTab projectId={id} isAdmin={canManageFiles} members={data.members} />
        </TabsContent>

        <TabsContent value="tasks" className="pt-6">
          <SectionCard title={canSeePrivate ? "Tasks" : "My Tasks"} subtitle={canSeePrivate ? undefined : "Showing only tasks assigned to or created by you"}>
            <div className="space-y-1">
              {visibleTasks.slice(0, 50).map((t) => (
                <div key={t.id} className="flex items-center gap-3 py-1.5 text-sm">
                  <Badge variant="outline" className="capitalize text-[10px] w-24 justify-center">{t.status.replace("_", " ")}</Badge>
                  <span className="flex-1 truncate">{t.title}</span>
                  <Badge variant="secondary" className="capitalize text-[10px]">{t.priority}</Badge>
                </div>
              ))}
              {visibleTasks.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-6">
                  {canSeePrivate ? "No tasks on this project yet." : "You don't have any tasks on this project."}
                </div>
              )}
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="repos" className="pt-6 space-y-6">
          {git && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <KpiCard label="Total Commits (90d)" value={git.totals.commits} />
                <KpiCard label="Pull Requests" value={git.totals.prs} />
                <KpiCard label="Contributors" value={git.totals.contributors} tone="primary" />
              </div>

              <SectionCard title="Repositories on this Project">
                <div className="grid md:grid-cols-2 gap-3">
                  {git.repositories.map((r) => {
                    const Icon = PLATFORM_ICON[r.platform] || Github;
                    return (
                      <div key={r.id} className="rounded-lg hairline p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Icon className="w-5 h-5 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{r.name}</div>
                            <div className="text-[11px] text-muted-foreground capitalize">{r.platform} · {r.sync_status || "idle"}</div>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => syncRepo(r.id)}><RefreshCw className="w-3 h-3 mr-1" />Sync</Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div><div className="label-caps text-[9px]">Commits</div><div className="number text-sm">{r.stats?.commits || 0}</div></div>
                          <div><div className="label-caps text-[9px]">PRs</div><div className="number text-sm">{r.stats?.pull_requests || 0}</div></div>
                          <div><div className="label-caps text-[9px]">Devs</div><div className="number text-sm">{r.stats?.contributors || 0}</div></div>
                        </div>
                      </div>
                    );
                  })}
                  {git.repositories.length === 0 && (
                    <div className="text-sm text-muted-foreground col-span-2 text-center py-6">
                      No repositories linked to this project yet. Add one from the Repositories page.
                    </div>
                  )}
                </div>
              </SectionCard>

              {git.trend?.length > 0 && (
                <SectionCard title="Repo Activity Trend" subtitle="Aggregated across all repos of this project">
                  <div className="h-64">
                    <ResponsiveContainer>
                      <BarChart data={git.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="commits" fill="hsl(211 100% 55%)" name="Commits" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="prs" fill="hsl(262 83% 62%)" name="PRs" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </SectionCard>
              )}

              <SectionCard title="Contributors on this Project" subtitle="Aggregated across all repos">
                <div className="space-y-2">
                  {git.contributors.slice(0, 15).map((c, i) => (
                    <div key={c.id + i} className="flex items-center gap-3 py-1.5 border-b border-border/40 last:border-0">
                      <div className="number w-6 text-center text-[11px] text-muted-foreground">#{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        {!c.mapped && <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-500">Unmapped</Badge>}
                      </div>
                      <div className="number text-sm">{c.commits}</div>
                      <div className="text-[10px] text-muted-foreground">commits · {c.prs} PRs</div>
                    </div>
                  ))}
                  {git.contributors.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No contributor data.</div>}
                </div>
              </SectionCard>
            </>
          )}
        </TabsContent>

        <TabsContent value="docs" className="pt-6">
          <SectionCard title="Documentation" subtitle="Knowledge docs linked to this project">
            <LinkedDocsTab entityType="project" entityId={data.project.id} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="members" className="pt-6">
          <SectionCard title="Members">
            <div className="grid md:grid-cols-2 gap-3">
              {data.members.map((mem) => (
                <div key={mem.id} className="flex items-center gap-3 p-3 rounded-lg hairline">
                  <div className="h-9 w-9 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-medium">
                    {mem.full_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{mem.full_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{mem.designation}</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
